import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { cn } from "@tine/designsystem-utils";
import type { Entry } from "contentful";
import { observer } from "mobx-react-lite";

import type { ContentfulTextAndImage } from "../../common/types/cmsTypes";
import type { TypeImageWithFocusSkeleton } from "../../common/types/contentful";
import { getModuleVisibility } from "../../common/utils/segmentUtils";

import segmentStore from "../../stores/cms/segmentStore";

import ImageWithFocus from "./ImageWithFocus";
import RichTextRenderer from "./RichTextRenderer";

type TextAndImageProps = {
   content: ContentfulTextAndImage;
};

const TextAndImage = ({ content }: TextAndImageProps) => {
   const inspectorProps = useContentfulInspectorMode();

   const isVisible = getModuleVisibility(content, segmentStore.currentCustomerProperties);

   if (!isVisible) {
      return null;
   }

   if (!content.fields) {
      return null;
   }

   const entryId = content.sys.id;
   const image = content.fields.image as Entry<TypeImageWithFocusSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>;
   const imagePosition = content.fields.imagePosition;
   const ratio = content.fields.imageSize ?? "Standard";

   const className = cn("flex flex-col items-center justify-center gap-6", "lg:flex-row", {
      "lg:flex-row": imagePosition === "Left",
      "lg:flex-row-reverse": imagePosition === "Right"
   });

   const childClassName = "lg:w-1/2";

   const imageClassName = cn(childClassName, "lg:pt-1", {
      "lg:w-1/3": ratio === "Liten",
      "lg:w-1/2": ratio === "Standard",
      "lg:w-2/3": ratio === "Stor"
   });

   return (
      <div className={className}>
         <div
            className={cn("w-full max-w-2xl", imageClassName)}
            {...inspectorProps({
               entryId: entryId,
               fieldId: "image"
            })}
         >
            {image && (
               <ImageWithFocus width={800} ratio="16x9" entryId={image.sys.id} imageFromParent={image} placement="belowFold" />
            )}
         </div>
         <div
            className={childClassName}
            {...inspectorProps({
               entryId: entryId,
               fieldId: "text"
            })}
         >
            <RichTextRenderer document={content.fields.text} context="text-and-image" trackingLocationName="Text and image" />
         </div>
      </div>
   );
};

export default observer(TextAndImage);
